import React, { useState, useRef, useEffect, useMemo } from "react";
import { PHONE_BRANDS } from "../lib/api";
import { Search, ChevronDown, Check, Smartphone, X } from "lucide-react";

const BRANDS = Object.keys(PHONE_BRANDS);

export default function PhoneModelPicker({ value, onChange, testId = "phone-model-picker" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeBrand, setActiveBrand] = useState("Todas");
  const wrapRef = useRef();
  const inputRef = useRef();

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const result = {};
    Object.entries(PHONE_BRANDS).forEach(([brand, models]) => {
      if (activeBrand !== "Todas" && activeBrand !== brand) return;
      const matches = models.filter((m) => !q || m.toLowerCase().includes(q));
      if (matches.length) result[brand] = matches;
    });
    return result;
  }, [query, activeBrand]);

  const totalMatches = Object.values(filtered).reduce((acc, arr) => acc + arr.length, 0);

  const onPick = (m) => {
    onChange(m);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-[#0EA5E9] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/30 outline-none font-medium text-left flex items-center justify-between gap-2 transition"
        data-testid="phone-model-trigger"
      >
        <span className="inline-flex items-center gap-2 truncate">
          <Smartphone size={16} className="text-[#0EA5E9] flex-shrink-0" />
          <span className="truncate">{value || "Selecciona tu modelo"}</span>
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-2 left-0 right-0 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[420px]" data-testid="phone-model-panel">
          <div className="px-3 pt-3 pb-2 border-b border-slate-100">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar modelo (ej: iPhone 13, Galaxy S24, Redmi Note...)"
                className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/30 outline-none text-sm"
                data-testid="phone-model-search"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Limpiar"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1 mt-2.5">
              <BrandChip label="Todas" active={activeBrand === "Todas"} onClick={() => setActiveBrand("Todas")} />
              {BRANDS.map((b) => (
                <BrandChip key={b} label={b} active={activeBrand === b} onClick={() => setActiveBrand(b)} />
              ))}
            </div>
          </div>

          <div className="overflow-y-auto flex-1 px-2 py-2">
            {totalMatches === 0 && (
              <div className="text-center py-10 text-sm text-slate-500">
                No encontramos ese modelo. Escríbenos por WhatsApp y lo agregamos al instante.
              </div>
            )}
            {Object.entries(filtered).map(([brand, models]) => (
              <div key={brand} className="mb-2">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{brand}</div>
                <ul>
                  {models.map((m) => (
                    <li key={m}>
                      <button
                        type="button"
                        onClick={() => onPick(m)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium hover:bg-sky-50 transition flex items-center justify-between ${value === m ? "bg-sky-50 text-[#0EA5E9]" : "text-slate-800"}`}
                        data-testid={`phone-model-option-${m.replace(/\s+/g, "-")}`}
                      >
                        <span>{highlight(m, query)}</span>
                        {value === m && <Check size={15} className="text-[#0EA5E9]" />}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500 bg-slate-50">
            {totalMatches} modelo{totalMatches === 1 ? "" : "s"} disponibles
          </div>
        </div>
      )}
    </div>
  );
}

const BrandChip = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition ${active ? "bg-black text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}
    data-testid={`brand-chip-${label}`}
  >
    {label}
  </button>
);

function highlight(text, q) {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-sky-200 text-black rounded px-0.5">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}
